import React, { Component, Fragment } from 'react';
import { Observer, observer } from 'mobx-react';
import _ from 'lodash';
import { AutoSizer, MultiGrid } from 'react-virtualized';

import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { ComboBox } from 'office-ui-fabric-react/lib/ComboBox';
import { Icon } from 'office-ui-fabric-react/lib/Icon';

import { localizable } from 'components/Localization';
import emailValidator from 'helpers/emailValidator';

const config =
  require(`../../../../@${process.env.REACT_APP_TENANT}/config.js`).default;

/*

props.store = BulkRegistrationStore()
props.store.items = [{
  _selected,
  firstName: item.first_name,
  lastName: item.last_name,
  email,
  phone,
  salutation,
  title,
  offerViewing,
  _additional: {
    buyer_street,
    buyer_zip,
    buyer_city,
    buyer_country,
  },
  optionalUploads: [...],
  requiredUploads: [...],
}, ...]

props.t

*/

@localizable
@observer
class BulkRegistrationTable extends Component {
  componentDidMount() {
    const { store } = this.props;
    store.onSave = this.handleSave;
  }

  renderColumnHeader = (column) => {
    const { t } = this.props;
    if (column.key === '_select') {
      return (
        <Checkbox
          defaultChecked={true}
          onChange={this.handleColumnHeaderSelectCheckboxChanged()}
          title={t(`Select / unselect all`)}
        />
      );
    } else if (column.key.startsWith('uploadtype-')) {
      const type = column.key.substr('uploadtype-'.length);
      return (
        <Checkbox
          label={
            <Fragment>
              <span style={{ fontWeight: 'bold' }}>
                {t(`uploadtype::${type}`).substr(0, 1)}
              </span>
              {column.footnote_index && (
                <sup style={{ marginLeft: '4px', fontSize: 'x-small' }}>
                  {column.footnote_index})
                </sup>
              )}
            </Fragment>
          }
          defaultChecked={
            !column.optional && config.defaultUploadTypes.includes(type)
          }
          disabled={config.requiredUploadTypes.includes(type)}
          onChange={this.handleColumnHeaderUploadTypeCheckboxChanged(
            type,
            column.optional
          )}
          title={t(`uploadtype::${type}`)}
          styles={{
            label: {
              paddingRight: '10px',
            },
          }}
        />
      );
    } else
      return (
        <Fragment>
          {column.title}{' '}
          {column.required && <sup style={{ color: 'Red' }}>*</sup>}
          {column.footnote && column.footnote_index && (
            <sup style={{ marginLeft: '4px', fontSize: 'x-small' }}>
              {column.footnote_index})
            </sup>
          )}
        </Fragment>
      );
  };

  handleColumnHeaderSelectCheckboxChanged = () => (ev, isChecked) => {
    const { store } = this.props;
    for (var i = 0; i < store.count; i++)
      store.change(i, '_selected', isChecked);
  };

  handleColumnHeaderUploadTypeCheckboxChanged =
    (type, optional) => (ev, isChecked) => {
      const { store } = this.props;
      for (var i = 0; i < store.count; i++)
        this.handleUploadsChanged(i, type, optional, isChecked);
    };

  renderItemColumn = (item, index, column) => {
    const { store, t } = this.props;

    if (column.key === '_validation') {
      let errors = [];
      store.columns.forEach((_column) => {
        if (
          !_column.key.startsWith('_') &&
          _column.fieldName &&
          !_column.readonly
        ) {
          const error = this.fieldValidation(_column)(item[_column.fieldName]);
          if (error) errors.push(`${_column.title}: ${error}`);
        }
      });
      if (item._error) errors.push(`${t('Saving error')}: ${item._error}`);
      if (errors.length > 0)
        return (
          <IconButton
            iconProps={{ iconName: 'Warning' }}
            title={errors.join('\n')}
            styles={{ root: { color: '#a80000' } }}
            tabIndex={-1}
          />
        );
      return '';
    } else if (column.key === '_remove') {
      return (
        <IconButton
          iconProps={{ iconName: 'Clear' }}
          title="Remove"
          ariaLabel="Remove"
          onClick={this.handleRemove(index)}
          tabIndex={-1}
        />
      );
    } else if (column.key === '_select') {
      return (
        <Checkbox
          checked={item._selected}
          onChange={this.handleSelectCheckboxChanged(index)}
        />
      );
    } else if (column.key === '_additional') {
      const additional = _.map(store.additionalFields, (field, key) =>
        field.title && !!item[key] ? `${field.title}: ${item[key]}` : null
      );
      const content = _.filter(additional, (value) => !!value).join('\n');
      return <Icon iconName="More" title={content} />;
    } else if (column.key.startsWith('uploadtype-')) {
      const type = column.key.substr('uploadtype-'.length);
      return (
        <Checkbox
          checked={
            column.optional
              ? item.optionalUploads.includes(type)
              : item.requiredUploads.includes(type)
          }
          disabled={config.requiredUploadTypes.includes(type)}
          onChange={this.handleUploadsCheckboxChanged(
            index,
            type,
            column.optional
          )}
        />
      );
    } else {
      const content = item[column.fieldName];
      const name = `row[${index}].${column.key}`;

      if (column.readonly) {
        if (column.type === 'checkbox')
          return (
            <div style={{ width: '100%', textAlign: 'center' }}>
              {content ? <Icon iconName="SkypeCheck" /> : ''}
            </div>
          );
        const text =
          column.options && content
            ? t(`${column.fieldName}::${content}`)
            : content;
        return (
          <div
            style={{
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={text}
          >
            {text}
          </div>
        );
      }

      return (
        <Fragment>
          {column.type === 'dropdown' && (
            <Dropdown
              name={name}
              defaultSelectedKey={content}
              onChange={this.handleDropdownChange(index, column.fieldName)}
              options={[{ key: '', text: '' }].concat(
                column.options.map((key) => {
                  return { key: key, text: t(`${column.fieldName}::${key}`) };
                })
              )}
            />
          )}
          {column.type === 'combobox' && (
            <ComboBox
              name={name}
              allowFreeform={true}
              autoComplete="nope"
              onChange={this.handleComboBoxChange(index, column.fieldName)}
              text={content ? t(`${column.fieldName}::${content}`) : ''}
              options={[{ key: '', text: '' }].concat(
                column.options.map((key) => {
                  return { key: key, text: t(`${column.fieldName}::${key}`) };
                })
              )}
            />
          )}
          {column.type === 'checkbox' && (
            <Checkbox
              name={name}
              autoComplete="nope"
              defaultChecked={content}
              onChange={this.handleCheckboxChange(index, column.fieldName)}
            />
          )}
          {(column.type === 'textfield' ||
            column.type === 'email' ||
            column.type === 'phone') && (
            <TextField
              name={name}
              onChange={this.handleChange(index, column.fieldName)}
              defaultValue={content}
              styles={{
                fieldGroup: {
                  borderColor: this.fieldValidation(column)(content)
                    ? '#A80000'
                    : '',
                },
              }}
              autoComplete="nope"
            />
          )}
        </Fragment>
      );
    }
  };

  fieldValidation = (column) => {
    if (column.type === 'email') return this.emailValidation;
    return () => '';
  };

  emailValidation = (value) => {
    if (!value) return '';
    const {
      t,
      store: { usedEmails },
    } = this.props;
    if (!emailValidator(value)) return t('Invalid email');
    if (usedEmails && usedEmails.includes(value))
      return t('Already registered');
    return '';
  };

  handleRemove = (index) => () => {
    const { store } = this.props;
    store.remove(index);
  };

  handleChange = (index, fieldName) => (ev, value) => {
    const { store } = this.props;
    store.change(index, fieldName, value);
  };

  handleSelectCheckboxChanged = (index) => (ev, isChecked) => {
    const { store } = this.props;
    store.change(index, '_selected', isChecked);
  };

  handleDropdownChange = (index, fieldName) => (ev, option, _index, value) => {
    const { store } = this.props;
    if (option) store.change(index, fieldName, option.key);
    else if (value) store.change(index, fieldName, value);
  };

  handleComboBoxChange = (index, fieldName) => (ev, option, _index, value) => {
    const { store } = this.props;
    if (option) store.change(index, fieldName, option.key);
    else if (value) store.change(index, fieldName, value);
  };

  handleCheckboxChange = (index, fieldName) => (ev, isChecked) => {
    const { store } = this.props;
    store.change(index, fieldName, isChecked);
  };

  handleUploadsCheckboxChanged = (index, type, optional) => (ev, isChecked) => {
    if (!config.requiredUploadTypes.includes(type))
      this.handleUploadsChanged(index, type, optional, isChecked);
  };

  handleUploadsChanged = (index, type, optional, isSelected) => {
    const { store } = this.props;
    store.changeUploads(index, type, optional, isSelected);
  };

  handleResize = (size) => {
    this.width = size.width;
    this.ensureColumnWidths();
    if (this.multiGrid) this.multiGrid.recomputeGridSize();
  };

  handleSave = () => {
    this.ensureColumnWidths();
    if (this.multiGrid) this.multiGrid.recomputeGridSize();
  };

  ensureColumnWidths = (width = null) => {
    const { store } = this.props;
    if (width) this.width = width;
    let columns = store.columns.map((column) => {
      return {
        width: column.width,
        minWidth: column.minWidth || 0,
      };
    });
    let desiredWidth = this.width - 30; // scrollBarWidth
    let currentWidth = _.sumBy(columns, 'width');
    if (currentWidth < desiredWidth) {
      // extend columns
      columns.forEach((column) => {
        column.width = Math.trunc((column.width / currentWidth) * desiredWidth);
      });
    } else {
      // shrink columns
      while (currentWidth > desiredWidth) {
        // remove columns that are already at their minimal width
        let count = 0;
        let desiredWidth2 = desiredWidth;
        let currentWidth2 = 0;
        columns.forEach((column) => {
          if (column.width > column.minWidth) {
            // column can still be shrinked
            count++;
            currentWidth2 += column.width;
          } else {
            // column already at minimum width -> exclude it from further calculation
            desiredWidth2 -= column.width;
          }
        });
        if (count === 0) break; // no more shrinkable column left
        // now shrink all shrinkable columns
        columns.forEach((column) => {
          if (column.width > column.minWidth)
            column.width = Math.max(
              Math.trunc((column.width / currentWidth2) * desiredWidth2),
              column.minWidth
            );
        });
        currentWidth = _.sumBy(columns, 'width');
      }
    }
    this.columnWidths = columns.map((column) => column.width);
  };

  getColor = (item) => {
    const { store } = this.props;
    if (!store.isItemComplete(item)) {
      return '#ffb3b3';
    }
    if (store.isItemDuplicate(item)) {
      return '#ffff99';
    }
    return '#dff6dd';
  };

  render() {
    const { store } = this.props;

    return (
      <div style={{ width: '100%', height: '99%' }}>
        <AutoSizer onResize={this.handleResize}>
          {({ height, width }) => (
            <MultiGrid
              ref={(element) => (this.multiGrid = element)}
              width={width}
              height={height}
              columnCount={store.columns.length}
              columnWidth={({ index }) => {
                this.ensureColumnWidths(width);
                return this.columnWidths[index];
              }}
              fixedRowCount={1}
              rowCount={store.count + 1}
              rowHeight={({ index }) => {
                // if (index === 0) return 40;
                //if (this.emailValidation(store.items[index - 1].email))
                //  return 80;
                return 40;
              }}
              estimatedRowSize={40}
              cellRenderer={({ columnIndex, key, rowIndex, style }) => (
                <Observer
                  key={key}
                  render={() => (
                    <Fragment>
                      {rowIndex === 0 && (
                        <div style={style}>
                          <div style={{ padding: '5px' }}>
                            {this.renderColumnHeader(
                              store.columns[columnIndex]
                            )}
                          </div>
                        </div>
                      )}
                      {rowIndex > 0 && rowIndex <= store.items.length && (
                        <div
                          style={_.assign({}, style, {
                            backgroundColor: this.getColor(
                              store.items[rowIndex - 1]
                            ),
                          })}
                        >
                          <div style={{ padding: '5px' }}>
                            {this.renderItemColumn(
                              store.items[rowIndex - 1],
                              rowIndex - 1,
                              store.columns[columnIndex]
                            )}
                          </div>
                        </div>
                      )}
                    </Fragment>
                  )}
                />
              )}
            />
          )}
        </AutoSizer>
      </div>
    );
  }
}

export default BulkRegistrationTable;



// WEBPACK FOOTER //
// ./src/app/containers/Sale/OffersSection/BulkRegistration/BulkRegistrationTable.jsx